import Head from 'expo-router/head';
import * as AppConstants from '@/utilities/AppConstants';
import { BasicWrapperView } from '@/components/BasicWrapperView';
import { RabblsSearch } from '@/components/RabblsSearch';
import { CustomText } from '@/components/CustomText';
import { StyleSheet, View } from 'react-native';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Community, UNKNOWN_COMMUNITY } from '@/types/community';
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import { BasicScrollView } from '@/components/BasicScrollView';
import { Panel } from 'react95-native-rabbl';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { Button, Hourglass } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { AppContext } from '@/components/AppContext';
import { CustomDivider } from '@/components/CustomDivider';
import { CustomDateTimePickerButton } from '@/components/CustomDateTimePickerButton';
import { Image } from 'expo-image';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

const min = new Date();
// JavaScript handles overflow
min.setDate(min.getDate() + 2);

const max = new Date();
max.setMonth(max.getMonth() + 3);

export default function RequestCollab() {
  const { c } = useLocalSearchParams<{ c: string }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(UNKNOWN_COMMUNITY);
  const { manage_community_id } = useLocalSearchParams<{ manage_community_id: string }>();
  const { showSpinner, hideSpinner } = useContext(AppContext);
	const [selectedDate, setSelectedDate] = useState<Date>(max);
  const router = useRouter();
  
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      AppUtilities.info("RequestCollab.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "RequestCollab.loadInitialData",
        "/rabbl/api/communityinfo",
        "POST",
        {
          community_uniqueid: c,
        }
      );
      AppUtilities.info("RequestCollab.loadInitialData finished:");
      AppUtilities.dir((response as any).body);
      const communityResult: Community = ((response as any).body as any).community;
      setCommunity(communityResult);
    } catch (e) {
      AppUtilities.handleException(e, "RequestCollab.loadInitialData");
      setError(AppUtilities.getErrors(e));
    } finally {
      setLoading(false);
    }
  }, [c]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();
      setError('');

      const response = await AppUtilities.fetchAuthorized(
        "RequestCollab.onSubmit",
        "/rabbl/api/requestcollab",
        "POST",
        {
          request_community_uniqueid: manage_community_id,
          target_community_uniqueid: c,
          expires: selectedDate?.toISOString(),
        }
      );

      AppUtils.showInformationalToast("Collab requested");

      router.replace(("/manage/" + manage_community_id) as RelativePathString);

    } catch (e : any) {
      AppUtilities.handleException(e, "RequestCollab.onSubmit");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
    } finally {
      hideSpinner();
    }
  }, [manage_community_id, c, selectedDate]);

  useEffect(() => {
    if (c) {
      loadInitialData();
    }
  }, [c, loadInitialData]);

  if (loading) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <Hourglass size={AppConstants.LARGE_HOURGLASS_SIZE} />
      </View>
    );
  }

  if (error.length > 0 || !AppUtils.isValidCommunity(community)) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <CustomText>Failed to load</CustomText>
        <AppText>{error}</AppText>
        <Button onPress={loadInitialData}>Retry</Button>
      </View>
    );
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Request collab"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <CustomText>Request Collab</CustomText>
          <AppText>When you click Request, an email will be sent to the owner of the {community.name} Rabbl.</AppText>
          <AppText>If they approve, you'll get an email confirmation and the collab will start for the users.</AppText>
          <CustomDivider />
          <AppText>Collab expiration (max 3 months but you can start another one after that):</AppText>
          <View style={{ flexDirection: "row", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
            <Image source={require("@/assets/images/PixelarticonsCalendarMonth32x32.png")} style={{ width: 32, height: 32 }} />
            <AppText style={{ fontWeight: "bold" }}>{selectedDate?.toLocaleDateString()}</AppText>
            <CustomDateTimePickerButton
              minDate={min}
              maxDate={max}
              selectedDate={selectedDate}
              onPickDate={(date) => setSelectedDate(date)}
              buttonText="Change expiration"
            />
          </View>
          <CustomDivider />
          <Button onPress={onSubmit}>Send Collab Request</Button>
        </View>
      </Panel>
    </BasicScrollView>
  );
}
